import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, Link, Menu, MenuItem, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';

import {
  AccessControlListModal,
  AccessControlListModalHandle,
  EditAccessModal,
  EditAccessModalHandle,
} from 'src/components/AccessControlModal';
import { DataTable } from 'src/components/DataTable';
import { DepositToPublishModal } from 'src/components/DepositToPublishModal';
import { ProjectStageEnum } from 'src/global/enums/project.enum';
import { MemberRole, MemberSchemaType } from 'src/global/schemas/member.schema';
import { ProjectListItem } from 'src/global/schemas/project.schema';
import { colors } from 'src/global/styles/colors';

interface StagingListTableProps {
  nodeList: Array<ProjectListItem>;
  isLoading: boolean;
  page: number;
  limit: number;
}

const ProductionListTable = (props: StagingListTableProps) => {
  const { nodeList, isLoading, page, limit } = props;
  const navigate = useNavigate();
  const { address } = useAccount();
  const [menuState, setMenuState] = React.useState<{ anchorEl: null | HTMLElement; selectedId: string | null }>({
    anchorEl: null,
    selectedId: null,
  });

  const accessControlModalRef = useRef<AccessControlListModalHandle>(null);
  const editAccessModalRef = useRef<EditAccessModalHandle>(null);
  const [selectedMember, setSelectedMember] = useState<MemberSchemaType | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [showDepositModal, setShowDepositModal] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    setMenuState({ anchorEl: event.currentTarget, selectedId: id });
  };

  const handleClose = () => {
    setMenuState({ anchorEl: null, selectedId: null });
  };

  const handleEditProd = () => {
    if (menuState.selectedId) {
      navigate(`/core-sale/${menuState.selectedId}/edit`);
      handleClose();
    }
  };

  const handleGoToAnalytics = (sale?: string) => {
    if (sale) {
      navigate(`/core-sale/${sale}/analytics`);
      handleClose();
    }
  };

  const handleAccessControl = () => {
    if (menuState.selectedId) {
      setSelectedProjectId(menuState.selectedId);
      accessControlModalRef.current?.show();
      handleClose();
    }
  };

  const handleEditMember = (member: MemberSchemaType) => {
    setSelectedMember(member);
    editAccessModalRef.current?.show();
  };

  const handleAddMember = () => {
    setSelectedMember(null);
    editAccessModalRef.current?.show();
  };

  const handleEditAccessSuccess = () => {
    setSelectedMember(null);
  };

  const handleNavigateDetail = (id: string) => {
    navigate(`/core-sale/${id}`);
  };

  const handleDepositToPublish = () => {
    if (menuState.selectedId) {
      setSelectedProjectId(menuState.selectedId);
      setShowDepositModal(true);
      handleClose();
    }
  };

  const handleDepositSuccess = (_productionUrl: string) => {
    setShowDepositModal(false);
    toast.success('The transaction hash has been successfully verified');
    navigate('/core-sale');
  };

  const renderStage = (stage: ProjectStageEnum) => {
    const stageConfig = {
      [ProjectStageEnum.PRODUCTION]: {
        text: 'Done',
        styles: {
          backgroundColor: '#17A9750F',
          color: colors.green300,
        },
      },
      [ProjectStageEnum.DEPOSIT]: {
        text: 'Waiting for payment',
        styles: {
          backgroundColor: '#F0C7230A',
          color: '#F0C723',
        },
      },
    };

    const { text, styles } = stageConfig[stage] || {
      text: '',
      styles: {},
    };

    return (
      <Box
        sx={{
          borderRadius: '4px',
          padding: '8px 12px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
          width: 'fit-content',
          ...styles,
        }}>
        {text}
      </Box>
    );
  };

  return (
    <>
      <DataTable
        isEmpty={!nodeList.length && !isLoading}
        isLoading={isLoading}
        skeletonRowCount={4}
        renderHeader={() => (
          <>
            <TableRow
              sx={{
                backgroundColor: colors.neutral06,
              }}>
              <TableCell
                rowSpan={2}
                sx={{
                  paddingLeft: '56px',
                }}>
                Project Name
              </TableCell>
              <TableCell rowSpan={2}>Last Updated</TableCell>
              <TableCell rowSpan={2}>Start Sale Date</TableCell>
              <TableCell rowSpan={2}>Status</TableCell>
              <TableCell rowSpan={2}>Current Tier</TableCell>
              <TableCell rowSpan={1}>Total Supply</TableCell>
              <TableCell rowSpan={1}>Total Sold</TableCell>
            </TableRow>
          </>
        )}
        renderBody={() => (
          <>
            {nodeList.map((row, index) => {
              const isCreatorOrAdmin =
                row.creator.name?.toLocaleLowerCase() === address?.toLocaleLowerCase() &&
                row.members.some(
                  member =>
                    member.addr?.toLocaleLowerCase() === address?.toLocaleLowerCase() &&
                    member.role === MemberRole.ADMIN,
                );
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <Stack direction={'row'} alignItems={'center'}>
                      <Typography fontSize={'14px'} width={'40px'}>
                        {(page - 1) * limit + index + 1}
                      </Typography>
                      <Stack>
                        <Typography
                          sx={{ cursor: 'pointer' }}
                          component={'p'}
                          onClick={() => handleNavigateDetail(row.id)}
                          fontSize={'14px'}>
                          {row.name}
                        </Typography>
                        {row.state === ProjectStageEnum.PRODUCTION ? (
                          <Link
                            component={'a'}
                            fontSize={'12px'}
                            textOverflow={'ellipsis'}
                            href={row.nodeSaleUrl}
                            color={'#64B4FF'}
                            target={'_blank'}>
                            {row.nodeSaleUrl}
                          </Link>
                        ) : (
                          <Typography component={'p'} fontSize={'12px'} color={'#19538a'}>
                            {row.nodeSaleUrl}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>{row.updatedAt ? format(new Date(row.updatedAt), 'dd/MM/yyyy') : '-'}</TableCell>
                  <TableCell>{row.releaseAt ? format(new Date(row.releaseAt!), 'dd/MM/yyyy H:mm') : '-'}</TableCell>
                  <TableCell align='center'>{renderStage(row.state)}</TableCell>
                  <TableCell align='center'>
                    {Number(row.setting?.currentTier ?? 0) + 1} / {Number(row.setting?.totalTier ?? 0)}
                  </TableCell>
                  <TableCell align='center'>{row.setting?.totalSupply}</TableCell>

                  <TableCell align='justify'>
                    <Box width={'100%'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography>{row.setting?.totalSold}</Typography>
                      <Button disableRipple onClick={e => handleClick(e, row.id)}>
                        <MoreHorizIcon sx={{ color: 'white' }} />
                      </Button>
                      <Menu
                        id={`menu-${row.id}`}
                        anchorEl={menuState.anchorEl}
                        open={Boolean(menuState.anchorEl && menuState.selectedId === row.id)}
                        onClose={handleClose}
                        sx={{
                          '& .MuiPaper-root': {
                            backgroundColor: 'black',
                            color: 'white',
                            borderRadius: '8px',
                          },
                          '& .MuiMenuItem-root': {
                            '&:hover': {
                              opacity: 0.7,
                            },
                          },
                        }}
                        MenuListProps={{
                          sx: {
                            bgcolor: 'black',
                            color: 'white',
                            borderRadius: '8px',
                          },
                        }}>
                        {row.state === ProjectStageEnum.DEPOSIT ? (
                          <MenuItem
                            onClick={handleDepositToPublish}
                            sx={{ padding: '8px', display: 'flex', flexDirection: 'column' }}>
                            <Box width={'100%'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
                              <AccountBalanceWalletIcon
                                sx={{ width: '20px', height: '20px', color: 'white', marginRight: '12px' }}
                              />
                              <Typography fontSize={'14px'} fontWeight={400} lineHeight={'20px'}>
                                Deposit to Publish
                              </Typography>
                            </Box>
                          </MenuItem>
                        ) : null}
                        <MenuItem
                          onClick={handleEditProd}
                          sx={{ padding: '8px', display: 'flex', flexDirection: 'column' }}>
                          <Box width={'100%'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
                            <BorderColorIcon
                              sx={{ width: '20px', height: '20px', color: 'white', marginRight: '12px' }}
                            />
                            <Typography fontSize={'14px'} fontWeight={400} lineHeight={'20px'}>
                              Edit Production
                            </Typography>
                          </Box>
                        </MenuItem>
                        {/* <MenuItem
                          onClick={() => handleGoToAnalytics(row.sale)}
                          sx={{ padding: '8px', display: 'flex', flexDirection: 'column' }}>
                          <Box width={'100%'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
                            <AnalyticsIcon
                              sx={{ width: '20px', height: '20px', color: 'white', marginRight: '12px' }}
                            />
                            <Typography fontSize={'14px'} fontWeight={400} lineHeight={'20px'}>
                              Analytics
                            </Typography>
                          </Box>
                        </MenuItem> */}
                        {isCreatorOrAdmin ? (
                          <MenuItem
                            onClick={handleAccessControl}
                            sx={{ padding: '8px', display: 'flex', flexDirection: 'column' }}>
                            <Box width={'100%'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
                              <SettingsIcon
                                sx={{ width: '20px', height: '20px', color: 'white', marginRight: '12px' }}
                              />
                              <Typography fontSize={'14px'} fontWeight={400} lineHeight={'20px'}>
                                Access Control
                              </Typography>
                            </Box>
                          </MenuItem>
                        ) : null}
                      </Menu>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </>
        )}
      />

      <AccessControlListModal
        ref={accessControlModalRef}
        projectId={selectedProjectId}
        onEditMember={handleEditMember}
        onAddMember={handleAddMember}
      />

      <EditAccessModal
        ref={editAccessModalRef}
        projectId={selectedProjectId}
        member={selectedMember}
        onSuccess={handleEditAccessSuccess}
      />

      <DepositToPublishModal
        open={showDepositModal}
        programId={selectedProjectId}
        onClose={() => setShowDepositModal(false)}
        onSuccess={handleDepositSuccess}
      />
    </>
  );
};

export { ProductionListTable };
